import React from 'react'

import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '@/lib/utils.ts'

const choiceButtonVariants = cva(
  'cursor-pointer rounded-[4px] text-base text-gray-700 focus-visible:ring-1 focus-visible:ring-blue-700 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50',
  {
    variants: {
      shape: {
        tile: 'flex min-h-16 flex-col items-start gap-1 px-3 py-2.5 text-left',
        pill: 'h-9 px-3.5',
        filter: 'rounded-[20px] px-3 py-1 text-gray-800',
      },
      pressed: {
        true: 'border-2 border-blue-700 bg-blue-100',
        false: 'm-px border border-gray-300 bg-white hover:bg-gray-50',
      },
    },
    compoundVariants: [
      { shape: 'pill', pressed: true, className: 'font-bold text-blue-800' },
      { shape: 'filter', pressed: true, className: 'border-4 border-blue-600 font-bold' },
      { shape: 'filter', pressed: false, className: 'm-0 border-4 border-gray-200 bg-gray-50' },
    ],
    defaultVariants: { shape: 'pill', pressed: false },
  }
)

export interface ChoiceButtonProps
  extends
    Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'aria-pressed'>,
    VariantProps<typeof choiceButtonVariants> {
  readonly pressed: boolean
}

export const ChoiceButton = React.forwardRef<HTMLButtonElement, ChoiceButtonProps>(
  ({ className, shape, pressed, ...props }, ref) => (
    <button
      ref={ref}
      type="button"
      aria-pressed={pressed}
      className={cn(choiceButtonVariants({ shape, pressed }), className)}
      {...props}
    />
  )
)
